<template>
  <div>
    <a-data-table
      :headers="headers"
      :items="items"
      single-expand
      show-expand
      :search="search"
      dense
      :no-data-text="$t('noData')"
      :no-results-text="$t('emptySearchRecords')"
    >
      <template v-slot:item.title="{ item }">
        <div class="d-flex">
          <v-tooltip bottom max-width="700" content-class="tipTaskTitle">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="taskTitle"
              >
                {{ item.title }}
              </div>
            </template>
            <span>{{ item.title }}</span>
          </v-tooltip>
          <span class="mx-2" v-if="item.is_private">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" color="red" size="16"
                  >mdi-lock</v-icon
                >
              </template>
              <span>{{ $t("task.tipConfidentialityKey") }}</span>
            </v-tooltip>
          </span>
        </div>
      </template>
      <template v-slot:item.task_status="{ item }">
        <v-chip small :color="getFilterStatusColor(item)" dark>
          {{ getStatusLabel(item.status) }}
        </v-chip>
      </template>
      <template v-slot:item.data-table-expand="{ expand, isExpanded }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
              v-if="isExpanded"
              @click="expand(!isExpanded)"
              >mdi-chevron-up</v-icon
            >
            <v-icon v-bind="attrs" v-on="on" v-else @click="expand(!isExpanded)"
              >mdi-chevron-down</v-icon
            >
          </template>
          <span>{{ $t("task.tipExapandCollapse") }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.linkedMeetings="{ item }">
        <v-list-item
          v-for="(meeting, index) in item.linkedMeetings"
          :key="index"
          dense
          style="max-height: 28px; min-height: auto"
          class="pl-0"
        >
          <v-list-item-icon class="mr-0">
            <v-icon size="16" class="mb-3">mdi-calendar</v-icon>
          </v-list-item-icon>

          <v-list-item-content
            v-if="
              $security.checkMeetingTypeAccess(
                meeting.meetingTypeId || '',
                'ViewMeeting'
              )
            "
            class="my-0"
          >
            <router-link
              style="cursor: pointer; display: inline-flex"
              :to="`/meeting/${meeting.id}/task/tenant/${tenantId}`"
            >
              <v-tooltip bottom content-class="tipMeetingName" max-width="500">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    class="text-truncate"
                    style="max-width: 200px"
                  >
                    {{ meeting.meetingName }}
                  </div>
                </template>
                <span>{{ meeting.meetingName }}</span>
              </v-tooltip>
            </router-link>
          </v-list-item-content>
          <v-list-item-content v-else class="my-0">
            <v-tooltip bottom content-class="tipMeetingName" max-width="500">
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  class="text-truncate"
                  style="max-width: 200px"
                >
                  {{ meeting.meetingName }}
                </div>
              </template>
              <span>{{ meeting.meetingName }}</span>
            </v-tooltip>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-slot:item.dueDate="{ item }">
        {{ formatDate(new Date(item.dueDate), displayDateFormat) }}
      </template>
      <template v-slot:item.assignees="{ item }">
        <v-row no-gutters>
          <v-col cols="12" class="pt-2 pb-2">
            <v-list-item
              v-for="(assignee, index) in getAssigneesDetail(item.assignees)"
              :key="index"
              style="max-height: 28px; min-height: auto"
              class="pl-0"
            >
              <v-list-item-avatar size="20" class="my-0 mr-2">
                <v-img
                  v-if="assignee.profilePhoto"
                  contain
                  :src="assignee.profilePhoto"
                ></v-img>
                <v-img v-else src="@/assets/images/No-Photo.jpg"></v-img>
              </v-list-item-avatar>

              <v-list-item-content class="my-0">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-list-item-title
                      v-bind="attrs"
                      v-on="on"
                      class="text-truncate body-m"
                      style="max-width: 200px"
                      >{{
                        assignee.firstName
                          ? assignee.firstName + " " + assignee.lastName
                          : assignee.email
                      }}
                    </v-list-item-title>
                  </template>
                  <span>{{
                    assignee.firstName
                      ? assignee.firstName + " " + assignee.lastName
                      : assignee.email
                  }}</span>
                </v-tooltip>
              </v-list-item-content>
            </v-list-item>
          </v-col>
        </v-row>
      </template>
      <template v-slot:item.action="{ item }">
        <action-template
          @edit="onEdit(item.id)"
          @delete="onDelete(item)"
          @sendEmail="onSendEmailNotification(item)"
        ></action-template>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <v-container fluid>
            <v-row>
              <v-col cols="12">
                <div v-if="item.description !== ''" class="d-flex mb-2">
                  <strong>{{ $t("agenda.lblDescription") }}:&nbsp;</strong>
                  <span v-html="item.description"> </span>
                </div>
                <work-flow-status
                  :isMainView="isMainView"
                  :taskDetails="item"
                  @back="onBack($event, item.id)"
                  @next="onNext($event, item.id)"
                ></work-flow-status>
              </v-col>
              <v-col cols="12">
               <task-documents :task="item" :key="`task-docs-${item.id}`"></task-documents>
              </v-col>
            </v-row>
          </v-container>
        </td>
      </template>
    </a-data-table>
    <confirm-dialog ref="confirm"></confirm-dialog>
    <status-confirmation-dialog
      v-model="showConfirmationDialog"
      @change="onStatusChange"
    ></status-confirmation-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { formatDate } from "@/utils/util";
import ActionTemplate from "./ActionTemplate.vue";
import ConfirmDialog from "../ConfirmDialog.vue";
import WorkFlowStatus from "./WorkFlowStatus.vue";
import TaskDocuments from "./TaskDocuments.vue";
import StatusConfirmationDialog from "./StatusConfirmationDialog.vue";
import { createUUID } from "../../utils/util";

export default {
  components: {
    ActionTemplate,
    ConfirmDialog,
    WorkFlowStatus,
    StatusConfirmationDialog,
    TaskDocuments
  },
  name: "TaskTableView",
  props: {
    search: String,
    items: {
      type: Array,
      default: () => [],
    },
    isMainView: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showConfirmationDialog: false,
      taskDetails: null,
    };
  },
  computed: {
    ...gettersMap(),
    ...mapState({
      userList: (state) => state.people.userList,
      deletedUserList: (state) => state.meeting.deletedUserList,
    }),
    headers() {
      let columns = [
        {
          value: "data-table-expand",
          sortable: false,
        },
        {
          text: this.$t("task.lblTaskTitle"),
          align: "start",
          value: "title",
          class: "header header-text",
        },
        {
          text: this.$t("task.lblAssignedTo"),
          value: "assignees",
          align: "start",
          sortable: false,
          class: "header header-text",
          width: 220
        },
        {
          text: this.$t("task.lblStatus"),
          value: "task_status",
          align: "start",
          class: "header header-text",
          width: 100
        },
        {
          text: this.$t("task.lblDueDate"),
          value: "dueDate",
          class: "header header-text",
          width: 180
        },
        {
          text: this.$t("task.lblTaggedMeetings"),
          value: "linkedMeetings",
          sortable: false,
          class: "header header-text",
          width: 200
        },
      ];
      if (
        this.isMainView &&
        (this.$security.checkUserAccess("UpdateTask") ||
          this.$security.checkUserAccess("DeleteTask"))
      ) {
        columns.push({
          text: this.$t("task.lblActions"),
          value: "action",
          sortable: false,
          align: "center",
          class: "header header-text",
          width: 100
        });
      }
      return columns;
    },
    tenantId() {
      return sessionStorage.getItem("tenantId");
    },
    locale() {
      return this.$i18n.locale;
    },
    displayDateFormat() {
      return this.locale !== "ja" ? "dd-MM-yyyy h:mm aaa" : "yyyy-MM-dd HH:mm";
    },
  },
  methods: {
    ...mapActions({
      getTasksList: "task/getTasksList",
      deleteTask: "task/deleteTask",
      updateTaskAPI: "task/updateTask",
      getTask: "task/getTask",
      updateTaskStatus: "task/updateTaskStatus",
      getDeletedUsersListTemp: "meeting/getDeletedUsersListTemp",
    }),
    getAssigneesDetail,
    formatDate,
    onDelete,
    confirmDelete,
    onEdit,
    onNext,
    onBack,
    updateWorkflow,
    getFilterStatusColor,
    onSendEmailNotification,
    onStatusChangeConfirmation,
    onStatusChange,
    createUUID,
  },
};

async function onStatusChange(comment) {
  const { status, id } = this.taskDetails;
  await this.updateWorkflow(status, id, comment);
}

async function onSendEmailNotification(task) {
  this.$emit("sendEmail", task);
}

async function updateWorkflow(status, id, comment) {
  try {
    const statusObj = {
      taskId: id,
      statusData: {
        id: this.$auth.user.user_id,
        email: this.$auth.user.email,
        name: this.$auth.user.tenantUserName,
        updatedDate: new Date().toISOString(),
        status,
        comment,
      },
    };
    const result = await this.updateTaskStatus(statusObj);

    if (result.status) {
      const params = {
        message: this.$t("task.lblWorkFlowStatus"),
        messageType: "success",
      };
      this.taskDetails = null;
      this.$root.$emit("AlertMessage", params);
    }
  } catch (error) {
    console.log(error)
  }
}

function onStatusChangeConfirmation(status, id) {
  this.showConfirmationDialog = true;
}

async function onBack(status, id) {
  this.taskDetails = {
    status,
    id,
    isBack: true,
  };
  this.showConfirmationDialog = true;
  // await this.updateWorkflow(status, id);
}

async function onNext(status, id) {
  this.taskDetails = {
    status,
    id,
    isBack: false,
  };
  this.showConfirmationDialog = true;
  // await this.updateWorkflow(status, id);
}

function onEdit(id) {
  this.$root.$emit("onEditTask", id);
}

async function onDelete(item) {
  try {
    const answer = await this.confirmDelete(item.title);
    if (answer) {
      await this.deleteTask(item.id);
      const params = {
        message: this.$t("task.msgTaskDeleteSuccess"),
        messageType: "success",
      };
      this.$root.$emit("AlertMessage", params);
    }
  } catch (error) {
    console.log(error)
  }
}

async function confirmDelete(title) {
  if (
    await this.$refs.confirm.open(
      this.$t("agenda.hDeleteMessage"),
      this.$t("agenda.lblTaskDeleteMessage", {title: title})
    )
  ) {
    return true;
  }
}

function getAssigneesDetail(assignees) {
  let localUserList = [...this.userList];
  localUserList = localUserList.concat(this.deletedUserList);
  return assignees.map((item) => {
    const uData = localUserList.find((u) => u.id == item.id);
    return {
      id: item.id,
      email: item.email,
      firstName: uData ? uData.firstName : "",
      lastName: uData ? uData.lastName : "",
      profilePhoto: uData ? uData.profilePhoto : "",
    };
  });
}

function gettersMap() {
  return mapGetters({
    getStatusLabel: "task/getStatusLabel",
  });
}

function getFilterStatusColor(item) {
  if (item.statusColor) {
    return item.statusColor;
  }
  if (item.status === "completed") {
    return "success";
  }

  const duedate = item.dueDate;
  if (duedate == "-") {
    return "success";
  }
  var date1 = new Date();
  var date2 = new Date(duedate);

  var Difference_In_Time = date2.getTime() - date1.getTime();

  // To calculate the no. of days between two dates
  var Difference_In_Days = Difference_In_Time / (1000 * 3600 * 24);
  if (Difference_In_Days < 0) return "error";
  // else if (Difference_In_Days > 10) return "success";
  else if (Difference_In_Days > 5) return "info";
  else if (Difference_In_Days > 0) return "warning";
  else return "info";
}
</script>

<style scoped>
::v-deep
  .v-data-table
  > .v-data-table__wrapper
  tbody
  tr.v-data-table__expanded__row {
  background-color: #eeeeee !important;
}
.tipMeetingName {
  overflow-wrap: break-word;
}
.taskTitle {
  white-space: normal;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tipTaskTitle {
  margin-left: 80px;
}
</style>